import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Camera, Upload, X, PawPrint, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fileToBase64, timeAgo } from "@/lib/files";
import { Spinner } from "@/components/kit";
import BreedResultCard from "@/components/BreedResultCard";

export default function BreedIdentifier() {
  const { openVerify } = useOutletContext() || {};
  const [image, setImage] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const resultRef = useRef(null);

  const loadHistory = useCallback(async () => {
    setHistory(await apiFetch("/breed-identify/history").catch(() => []));
  }, []);

  useEffect(() => {
    apiFetch("/dogs").then((d) => setDogs(d || [])).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(await fileToBase64(file));
    setResult(null);
    e.target.value = "";
  };

  const analyze = async () => {
    if (!image || analyzing) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const res = await apiFetch("/breed-identify", {
        method: "POST",
        body: JSON.stringify({ image_base64: image }),
      });
      setResult(res);
      loadHistory();
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      if (err.status === 403 && err.detail?.code === "email_not_verified") {
        toast.error("Please verify your email to use the Breed Identifier.");
        openVerify?.();
      } else if (err.status === 429) toast.error(err.detail?.message || "You're going too quickly.");
      else toast.error(err.message || "Couldn't identify the breed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const removeItem = async (id) => {
    await apiFetch(`/breed-identify/${id}`, { method: "DELETE" }).catch(() => {});
    setHistory((h) => h.filter((s) => s.id !== id));
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="breed-identifier-page">
      <div className="mb-5">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#2D2B2A] flex items-center gap-2">
          <PawPrint size={28} color="#E86A45" /> Breed Identifier
        </h1>
        <p className="text-[#4A4746] mt-1">
          Snap a photo of your dog for an AI breed guess — especially handy for mixed-breed pups.
        </p>
      </div>

      {/* Capture card */}
      <div className="rounded-3xl border border-[#E8E3DA] bg-white p-5">
        {image ? (
          <div className="relative">
            <img src={image} alt="dog to identify" className="w-full max-h-80 object-contain rounded-2xl bg-[#F2EFE9]" data-testid="breed-preview" />
            <button
              data-testid="breed-clear-image"
              onClick={() => { setImage(null); setResult(null); }}
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-[#2D2B2A]/80 text-white flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center py-8">
            <div className="h-16 w-16 rounded-3xl bg-[#F6DED5] flex items-center justify-center mb-3">
              <PawPrint size={30} color="#E86A45" />
            </div>
            <p className="font-bold text-[#2D2B2A]">A clear, well-lit photo of your dog works best.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <label data-testid="breed-camera-btn" className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-[#E86A45] text-white font-extrabold px-6 py-3 hover:bg-[#d65f3c] transition-colors">
                <Camera size={18} /> Take a photo
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
              </label>
              <label data-testid="breed-upload-btn" className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-[#F2EFE9] text-[#4A4746] font-extrabold px-6 py-3 hover:bg-[#E8E3DA] transition-colors">
                <Upload size={18} /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={pick} />
              </label>
            </div>
          </div>
        )}

        {image && (
          <button
            data-testid="breed-analyze-btn"
            onClick={analyze}
            disabled={analyzing}
            className="w-full mt-4 rounded-full bg-[#E86A45] text-white font-extrabold py-3.5 flex items-center justify-center gap-2 hover:bg-[#d65f3c] disabled:opacity-60 transition-colors"
          >
            {analyzing ? <><Spinner size={20} className="!border-white/40 !border-t-white" /> Identifying…</> : <><Sparkles size={18} /> Identify breed</>}
          </button>
        )}
      </div>

      {/* Result */}
      {result && (
        <div ref={resultRef}>
          <BreedResultCard result={result} dogs={dogs} />
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-extrabold mb-3">Recent identifications</h2>
          <div className="space-y-2">
            {history.map((s) => (
              <div key={s.id} data-testid={`breed-history-${s.id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#E8E3DA]">
                {s.image_base64 && <img src={s.image_base64.startsWith("data:") ? s.image_base64 : `data:image/jpeg;base64,${s.image_base64}`} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{s.primary_breed}</p>
                  <p className="text-xs font-bold text-[#B54828]">
                    {s.is_mixed ? "Mixed breed" : "Best match"} · {timeAgo(s.created_at)}
                  </p>
                </div>
                <button data-testid={`breed-history-delete-${s.id}`} onClick={() => removeItem(s.id)} className="p-2 rounded-full text-[#B54828] hover:bg-[#F6DED5]">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
